import { ReactElement, useMemo } from 'react';
import { Paper, Skeleton, Typography } from '@mui/material';
import { Address } from 'viem';

import { mapDataToUndefinedIfSkip } from 'modules/api/utils';
import { useConnection } from 'modules/auth/hooks/useConnection';
import { TextButton } from 'modules/common/components/TextButton/TextButton';
import { ZERO } from 'modules/common/const';
import { useGetUSDAmount } from 'modules/common/hooks/useGetUSDAmount';
import { KnownDialogs, useDialog } from 'modules/dialogs';
import { useTranslation } from 'modules/i18n';
import { useGetOwnerSelfStakeAmountQuery } from 'modules/ownerPanel/actions/getSelfStakeAmount';
import { StatGroupItem } from 'modules/ownerPanel/components/StatGroupItem';
import { useGetAccountPoolQuery } from 'modules/pool/actions/getAccountPool';
import { useGetPoolQuery } from 'modules/pool/actions/getPool';
import { useGetPoolOwnerQuery } from 'modules/pool/actions/getPoolOwner';

import { translation } from './translation';
import { useStyles } from './useStyles';

interface IStatsProps {
  showDialogButtons?: boolean;
  poolAddress: Address;
}

export function Stats({
  poolAddress,
  showDialogButtons = false,
}: IStatsProps): ReactElement {
  const { t, keys } = useTranslation(translation);
  const { classes, cx } = useStyles();
  const { cacheTime } = useConnection();

  const { onOpen } = useDialog<string>(KnownDialogs.withdraw);

  const { data: pool } = useGetPoolQuery(
    { address: poolAddress },
    { refetchOnMountOrArgChange: cacheTime },
  );

  const { data: ownerAddress = '' } = useGetPoolOwnerQuery(
    { address: poolAddress },
    { refetchOnMountOrArgChange: cacheTime },
  );

  const { data: accountOwnerPool } = useGetAccountPoolQuery(
    {
      connectionKey: '',
      address: poolAddress,
      accountAddress: ownerAddress as Address,
    },
    {
      skip: !ownerAddress,
      selectFromResult: mapDataToUndefinedIfSkip,
      refetchOnMountOrArgChange: cacheTime,
    },
  );

  const { data: selfStakeAmount } = useGetOwnerSelfStakeAmountQuery(
    { poolAddress },
    { refetchOnMountOrArgChange: cacheTime },
  );

  const lockedAmount = useMemo(
    () => accountOwnerPool?.stakedAmount?.minus(selfStakeAmount ?? ZERO),
    [accountOwnerPool, selfStakeAmount],
  );

  const delegationsAmount = useMemo(
    () => pool?.tvl?.minus(accountOwnerPool?.stakedAmount ?? ZERO),
    [accountOwnerPool?.stakedAmount, pool?.tvl],
  );

  const tvlUSD = useGetUSDAmount(pool?.tvl);
  const selfStakeAmountUSD = useGetUSDAmount(selfStakeAmount);
  const stakedAmountUSD = useGetUSDAmount(accountOwnerPool?.stakedAmount);
  const lockedAmountUSD = useGetUSDAmount(lockedAmount);
  const delegationsAmountUSD = useGetUSDAmount(delegationsAmount);

  return (
    <div className={classes.root}>
      <Paper className={classes.tvlStat}>
        <StatGroupItem
          amount={pool?.tvl}
          label={t(keys.tvl)}
          usdAmount={tvlUSD}
        />
      </Paper>

      <Paper className={classes.multiStats}>
        <StatGroupItem
          amount={accountOwnerPool?.stakedAmount}
          className={classes.topSubStat}
          label={t(keys.selfStaked)}
          usdAmount={stakedAmountUSD}
          variant="h3"
        />

        <StatGroupItem
          amount={lockedAmount?.decimalPlaces(2)}
          className={classes.subStat}
          label={t(keys.locked)}
          usdAmount={lockedAmountUSD}
          variant="h5"
        />

        <StatGroupItem
          amount={selfStakeAmount?.decimalPlaces(2)}
          buttonSlot={
            showDialogButtons ? (
              <TextButton onClick={() => onOpen(poolAddress)}>
                {t(keys.withdraw)}
              </TextButton>
            ) : undefined
          }
          className={classes.subStat}
          label={t(keys.unlocked)}
          usdAmount={selfStakeAmountUSD}
          variant="h5"
        />
      </Paper>

      <Paper className={classes.multiStats}>
        <StatGroupItem
          amount={delegationsAmount}
          className={classes.topSubStat}
          label={t(keys.delegations)}
          usdAmount={delegationsAmountUSD}
          variant="h3"
        />

        <div className={cx(classes.subStat, classes.subItem)}>
          <Typography sx={{ opacity: 0.6, fontWeight: 600 }} variant="body1">
            {t(keys.delegators)}
          </Typography>

          <Typography variant="h5">
            {pool?.totalDelegators.toFormat() || <Skeleton />}
          </Typography>
        </div>
      </Paper>
    </div>
  );
}
